import { useState } from 'react';
import logTrailDetailsAddToListButtonTapped from '@alltrails/analytics/events/logTrailDetailsAddToListButtonTapped';
import BookmarkButton from '@alltrails/shared/components/BookmarkButton';
import CompletionBadge from '@alltrails/shared/components/cards/CompletionBadge';
import DifficultyAndRating from '@alltrails/shared/components/cards/DifficultyAndRating';
import { logTrailDetailsPhotoClicked, TrailDetailsPhotoLocations } from 'utils/trail_analytics_helpers';
import Trail, { TrailId } from 'types/Trails/Trail';
import { urlReformatPhotos } from 'utils/lightbox_helpers';
import AggRatingStructuredData, { aggRatingDataAttributes } from 'components/shared/AggRatingStructuredData';
import { getTrailProfilePhotoUrl } from 'utils/trail_helpers';
import Lightbox from 'components/shared/Lightbox';
import { defineMessages, useIntl } from '@alltrails/shared/react-intl';
import SignupModal from 'components/SignupModal';
import useToggle from '@alltrails/shared/hooks/useToggle';
import useUser from 'hooks/useUser';
import getTrailProps from 'api/Trail';
import CardLocation from '../CardLocation';
import * as styles from './TrailDetailsCard.module.scss';
import * as cardStyles from '../CardContent/styles/styles.module.scss';

const messages = defineMessages({
  altTitle: { defaultMessage: '{list} | AllTrails.com' }
});

type Props = {
  exploreFlag?: boolean;
  isPending?: boolean;
  listMethods: any;
  numPhotos?: number;
  photoId: number;
  photos?: any;
  photoUrl?: string;
  trail: Trail;
  webpPhotoUrl?: string;
};

export default function TrailDetailsCard({ isPending, photoUrl, webpPhotoUrl, trail, photos = [], photoId, listMethods }: Props) {
  const [isSignupModalOpen, toggleSignupModal] = useToggle();
  const intl = useIntl();
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const user = useUser();

  function handleCloseRequest() {
    setLightboxOpen(false);
  }

  function getLightboxProps() {
    return {
      images: urlReformatPhotos(photos),
      handleCloseRequest,
      isOpen: true,
      startingIndex: 0,
      trailId: trail.trail_id
    };
  }

  function openLightbox() {
    setLightboxOpen(true);
  }

  function renderLightbox() {
    if (!lightboxOpen) {
      return null;
    }

    return <Lightbox {...getLightboxProps()} />;
  }

  function onClick() {
    if (!lightboxOpen) {
      logTrailDetailsPhotoClicked(trail.trail_id, photoId, TrailDetailsPhotoLocations.HeroImage);
    }
    openLightbox();
  }

  const trailPhotoUrl = photoUrl || getTrailProfilePhotoUrl(trail);
  let displayName = trail.name;

  if (isPending) {
    displayName += ' [PENDING]';
  }

  const altTitle = intl.formatMessage(messages.altTitle, {
    // these fields are already translated from the server
    list: [trail.name, trail.area_name, trail.city_name, trail.state_name, trail.country_name].filter(Boolean).join(', ')
  });

  const trailHasReviews = trail.num_reviews > 0;

  return (
    <>
      <SignupModal
        isOpen={isSignupModalOpen}
        onRequestClose={() => {
          toggleSignupModal();
        }}
        onSuccess={async () => {
          const trailDetails = await getTrailProps(trail.ID as TrailId, false);
          listMethods.setListsAndListItems({ lists: trailDetails.initialMyLists, listItems: trailDetails.initialMyListItems });
          listMethods.setActiveItem({ type: trail.type, ID: trail.ID, objectID: trail.objectID });
          listMethods.toggleShowModal(true);
          toggleSignupModal();
        }}
      />
      <div
        className={['xlate-none', styles.card, photos.length > 0 ? styles.clickable : ''].join(' ')}
        itemProp="image"
        title={trail.name}
        {...(photos.length > 0 ? { onClick, role: 'button' } : {})}
      >
        {(webpPhotoUrl || trailPhotoUrl) && (
          <picture>
            <source srcSet={webpPhotoUrl} type="image/webp" />
            <img src={trailPhotoUrl} alt={altTitle} className={styles.backgroundImage} title={altTitle} />
          </picture>
        )}
        <div className={styles.content}>
          <div className={styles.badgeContainer}>
            <CompletionBadge isCompleted={listMethods.isComplete(trail.type, trail.ID)} isVerified={listMethods.isVerified(trail.type, trail.ID)} />
          </div>
          <h1 className={['xlate-none', styles.name].join(' ')} itemProp="name" title={trail.name}>
            {displayName}
          </h1>
          <div
            className={`${cardStyles.info} ${cardStyles.thin}`}
            {...(trailHasReviews ? aggRatingDataAttributes : {})}
            data-testid="difficulty-and-rating"
          >
            <DifficultyAndRating
              className={styles.difficultyAndRatingContainer}
              difficulty={parseInt(trail.difficulty_rating)}
              rating={trail.avg_rating}
              reviewCount={trail.num_reviews}
            />
            {trailHasReviews && <AggRatingStructuredData rating={trail.avg_rating} reviewCount={trail.num_reviews} />}
          </div>
          <CardLocation className={styles.location} trail={trail} />
        </div>
        <BookmarkButton
          className={styles.favorite}
          onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
            e.stopPropagation(); // make sure we don't display the lightbox

            logTrailDetailsAddToListButtonTapped({ trail_id: trail.trail_id });

            if (!user) {
              toggleSignupModal();
              return;
            }

            listMethods.setActiveItem({ type: trail.type, ID: trail.ID, objectID: trail.objectID });
            listMethods.toggleShowModal(true);
          }}
          isSelected={listMethods.isFavorite(trail.type, trail.ID)}
        />
        {renderLightbox()}
      </div>
    </>
  );
}
